/* eslint-disable @typescript-eslint/no-explicit-any */
import * as $protobuf from 'protobufjs';
import { every, hasIn, includes, invert, map, memoize } from 'es-toolkit/compat';
import { tocktix as libProto } from '@tock/protos/dist/types/lib';

import { protoLib } from './protoLib';

const { MessageSet } = protoLib.lookup('tocktix.lib') as any;

export function isProto(maybeProto: any) {
  return hasIn(maybeProto, '$type');
}

/*
 * Get the MessageSet extension name of the type by traversing the type hierarchy.
 */
export function getExtension(protobufType: $protobuf.Type): string {
  const extension = (protobufType as any)?.nested?.messageSetExtension?.extensionField?.name;
  if (!extension) {
    // eslint-disable-next-line no-console
    console.error('Not a messageSetExtension', protobufType);
    return null as any;
  }
  return extension;
}

/*
 * Convert a proto to JSON.
 */
export function toJSON<T>(maybeProto: $protobuf.Message | any): T {
  if (!isProto(maybeProto)) {
    // eslint-disable-next-line no-console
    console.warn(`Trying to convert a non proto to JSON: ${maybeProto}`);
    return maybeProto;
  }

  return maybeProto.$type.toObject(maybeProto, {
    longs: Number,
    enums: String,
    defaults: false,
    arrays: true,
    oneofs: true,
  }) as T;
}

/*
 * Convert a proto array to JSON array.
 */
export function toJSONArray<T>(maybeProto: $protobuf.Message[]): T[] {
  return map(maybeProto, toJSON) as T[];
}

/*
 * Convert a proto to array buffer.
 */
export function toArrayBuffer<T extends object>(proto: $protobuf.Message<T>): Uint8Array {
  if (!proto) {
    return undefined as any;
  }
  const verifyResult = proto.$type.verify(proto);
  if (verifyResult) {
    // eslint-disable-next-line no-console
    console.error(verifyResult);
    throw new Error(verifyResult);
  }
  const writer = $protobuf.Writer.create();
  proto.$type.encode(proto, writer);
  return writer.finish();
}

/*
 * Parse from a buffer for a given type.
 */
export function parseProto(buffer: any, protoType: $protobuf.Type) {
  // tslint:disable:strict-type-predicates
  const reader = $protobuf.Reader.create(
    typeof Uint8Array !== 'undefined' ? new Uint8Array(buffer) : buffer
  );
  return protoType.decode(reader);
}

/*
 * Convert a json object to a MessageSet wrapping the given type.
 */
export function asMessageSet(protobufType: $protobuf.Type, json: object) {
  const extension = getExtension(protobufType);
  const messageSet = MessageSet.create();
  const obj = protobufType.fromObject(json);
  messageSet[extension] = obj;
  return messageSet;
}

/*
 * Convert a proto object to a MessageSet wrapping the given type.
 */
export function protoAsMessageSet(protobufType: $protobuf.Type, obj: object) {
  const extension = getExtension(protobufType);
  const messageSet = MessageSet.create();
  messageSet[extension] = obj;
  return messageSet;
}

/*
 * Check if the MessageSet has an extension of the given type
 */
export function messageSetHas(messageSet: libProto.lib.MessageSet, protobufType: $protobuf.Type) {
  return !!messageSetGet(messageSet, protobufType);
}

/*
 * Get the object from MessageSet of the given type or null.
 */
export function messageSetGet(messageSet: any, protobufType: $protobuf.Type) {
  const extension = getExtension(protobufType);
  return messageSet[extension];
}

/*
 * Function that takes a maybe proto number and converts it to a primitive number if needed
 */
export function safeToNumber(maybeProtoNumber?: number | Long | null): number {
  if (!maybeProtoNumber) {
    return undefined as any;
  }
  return (typeof (maybeProtoNumber as Long).toNumber === 'function')
    ? (maybeProtoNumber as Long).toNumber()
    : (maybeProtoNumber as number);
}

const enumInvertCache = memoize((enumObj: $protobuf.Enum) => {
  return invert(enumObj);
});
/*
 * Convert an enum ordinal to a string
 */
export function toEnumString(
  enumObj: $protobuf.Enum,
  value: string | number | null | undefined
): string {
  if (typeof value === 'string') {
    return value;
  }

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return enumInvertCache(enumObj)[value!];
}

export function safeEnumCompare(
  objectVal: string | number | null | undefined | {},
  enumObj: $protobuf.Enum,
  enumVal: number
) {
  if (typeof objectVal === 'number') {
    return objectVal === enumVal;
  } else if (typeof objectVal === 'string') {
    return objectVal === toEnumString(enumObj, enumVal);
  } else {
    return false;
  }
}

export function safeIncludesEnum(arrayToCheck: any, enumObj: $protobuf.Enum, enumVal: number) {
  if (every(arrayToCheck, (v: any) => typeof v === 'number')) {
    return includes(arrayToCheck, enumVal);
  } else if (every(arrayToCheck, (v: any) => typeof v === 'string')) {
    return includes(arrayToCheck, toEnumString(enumObj, enumVal));
  } else {
    throw new Error('safeIncludesEnum received array that was not all numbers or strings');
  }
}

//convert api payload to json
export function payloadToJSON<T>(payload: any): T {
  return payload
    ? Array.isArray(payload)
      ? toJSONArray<T>(payload)
      : isProto(payload)
      ? toJSON<T>(payload)
      : payload
    : payload;
}

export async function requestToJSON<T>(request: Promise<any>): Promise<T> {
  const result = await request;
  const parsed = payloadToJSON<T>(result.payload);
  return parsed;
}
